import { useState } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import Card from '../../components/base/Card';

export default function ServiceDetailPage() {
  const { serviceName } = useParams();
  const [selectedProject, setSelectedProject] = useState<number | null>(null);

  // Service data mapping
  const serviceData: Record<string, any> = {
    'autocad': {
      title: 'AutoCAD Solutions',
      subtitle: 'Delivering precise AutoCAD drawings for contractors and architects',
      heroImage: 'https://placehold.co/600x400',
      description: {
        what: 'Our AutoCAD solutions deliver precision-engineered 2D and 3D technical drawings, architectural plans, and detailed engineering schematics. We utilize industry-standard practices to create comprehensive documentation that meets all regulatory requirements and project specifications.',
        who: 'Perfect for architects developing building plans, contractors requiring construction documentation, engineers needing technical schematics, and project managers coordinating complex builds.',
        why: 'Accurate technical drawings are the foundation of successful projects. Our AutoCAD expertise ensures every dimension, specification, and detail is precisely documented, reducing errors, saving time, and ensuring compliance with industry standards.'
      },
      valueProps: [
        {
          icon: 'ri-ruler-2-line',
          title: 'Precision Accuracy',
          description: 'Every measurement and dimension verified to industry standards'
        },
        {
          icon: 'ri-time-line',
          title: 'Fast Turnaround',
          description: 'Efficient workflows delivering projects on schedule'
        },
        {
          icon: 'ri-shield-check-line',
          title: 'Industry Standards',
          description: 'Compliant with all relevant codes and regulations'
        },
        {
          icon: 'ri-git-branch-line',
          title: 'Multi-Platform Compatibility',
          description: 'Files compatible with all major CAD software platforms'
        },
        {
          icon: 'ri-customer-service-2-line',
          title: 'Expert Support',
          description: '21+ years of experience in technical drawing services'
        }
      ],
      portfolioProjects: [
        {
          title: 'Commercial Office Tower',
          caption: 'Complete architectural drawings for 20-story office complex',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Industrial Manufacturing Plant',
          caption: 'Detailed facility layout and equipment positioning',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Residential Development',
          caption: 'Multi-unit housing complex with site planning',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Healthcare Facility',
          caption: 'Specialized medical center layout and systems',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Educational Campus',
          caption: 'University building complex with infrastructure',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Retail Shopping Center',
          caption: 'Multi-tenant commercial space with parking',
          image: 'https://placehold.co/600x400'
        }
      ],
      caseStudy: {
        title: 'Metro Office Complex - 50-Story Tower',
        description: 'We provided comprehensive AutoCAD documentation for a landmark 50-story office tower in downtown metro area. The project required detailed floor plans for each level, structural drawings, MEP layouts, and site planning coordination. Our team delivered over 200 technical drawings within the 6-week timeline, enabling the construction team to begin work on schedule. The precision of our drawings reduced on-site conflicts by 85% compared to industry averages.',
        image: 'https://placehold.co/600x400'
      }
    },
    'revit': {
      title: 'Revit 3D Rendering Solutions',
      subtitle: 'Advanced BIM modeling with photorealistic 3D renderings for complex projects',
      heroImage: 'https://placehold.co/600x400',
      description: {
        what: 'Our Revit solutions combine advanced Building Information Modeling (BIM) with stunning photorealistic 3D renderings. We create comprehensive parametric models that integrate architectural, structural, and MEP systems while producing visualization-quality renders for presentations and marketing.',
        who: 'Ideal for architects requiring BIM coordination, developers needing marketing visuals, contractors managing complex builds, and design teams collaborating on large-scale projects.',
        why: 'BIM technology revolutionizes project coordination by providing a single source of truth for all project stakeholders. Our Revit expertise ensures accurate modeling, clash detection, and stunning visualizations that help secure project approvals and funding.'
      },
      valueProps: [
        {
          icon: 'ri-building-3-line',
          title: 'Advanced BIM Modeling',
          description: 'Comprehensive 3D models with full parametric capabilities'
        },
        {
          icon: 'ri-image-line',
          title: 'Photorealistic Renders',
          description: 'Marketing-quality visualizations and presentations'
        },
        {
          icon: 'ri-group-line',
          title: 'Collaboration Ready',
          description: 'Multi-disciplinary coordination and clash detection'
        },
        {
          icon: 'ri-database-2-line',
          title: 'Information Rich',
          description: 'Detailed material schedules and quantity takeoffs'
        },
        {
          icon: 'ri-refresh-line',
          title: 'Design Iterations',
          description: 'Rapid design changes with automatic updates'
        }
      ],
      portfolioProjects: [
        {
          title: 'Luxury Resort Complex',
          caption: 'Full BIM model with interior and exterior renderings',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Corporate Headquarters',
          caption: 'Multi-building campus with detailed interiors',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Mixed-Use Development',
          caption: 'Residential and commercial integration modeling',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Hospital Expansion',
          caption: 'Specialized healthcare facility with MEP coordination',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Stadium Complex',
          caption: 'Large-scale sports facility with structural details',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Transit Hub',
          caption: 'Transportation center with passenger flow modeling',
          image: 'https://placehold.co/600x400'
        }
      ],
      caseStudy: {
        title: 'Green Valley Medical Center - 300,000 sq ft Expansion',
        description: 'Our team created a comprehensive Revit model for a major medical center expansion, integrating complex MEP systems, specialized medical equipment, and stringent healthcare regulations. The BIM model enabled early clash detection, saving the project over $2.3M in potential rework costs. We delivered 45 photorealistic interior and exterior renderings that helped secure $180M in project funding from stakeholders and regulatory approval boards.',
        image: 'https://placehold.co/600x400'
      }
    },
    'dialux': {
      title: 'Dialux Lighting Design Solutions',
      subtitle: 'Professional lighting design and analysis for optimal illumination and energy efficiency',
      heroImage: 'https://placehold.co/600x400',
      description: {
        what: 'Our Dialux lighting design services provide comprehensive illumination analysis and energy-efficient lighting solutions. We use industry-standard Dialux software to create optimal lighting schemes that meet both aesthetic and functional requirements while ensuring code compliance.',
        who: 'Essential for architects designing interior spaces, facility managers optimizing energy costs, contractors installing lighting systems, and developers meeting sustainability requirements.',
        why: 'Proper lighting design impacts occupant comfort, productivity, and energy costs significantly. Our Dialux expertise ensures optimal illumination levels, energy efficiency, and regulatory compliance while creating visually appealing environments.'
      },
      valueProps: [
        {
          icon: 'ri-lightbulb-line',
          title: 'Optimal Illumination',
          description: 'Precise lighting calculations for perfect illumination levels'
        },
        {
          icon: 'ri-leaf-line',
          title: 'Energy Efficiency',
          description: 'Reduced energy consumption through smart lighting design'
        },
        {
          icon: 'ri-file-check-line',
          title: 'Code Compliance',
          description: 'Meeting all local and international lighting standards'
        },
        {
          icon: 'ri-bar-chart-line',
          title: 'Detailed Analysis',
          description: 'Comprehensive photometric reports and calculations'
        },
        {
          icon: 'ri-money-dollar-circle-line',
          title: 'Cost Optimization',
          description: 'Balanced approach between quality and budget requirements'
        }
      ],
      portfolioProjects: [
        {
          title: 'Art Gallery Exhibition',
          caption: 'Specialized museum lighting with UV protection',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Corporate Office Floors',
          caption: 'Energy-efficient workspace lighting solution',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Retail Showroom',
          caption: 'Dynamic lighting for product presentation',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Educational Library',
          caption: 'Study area lighting with glare control',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Healthcare Surgery Suite',
          caption: 'Specialized medical lighting requirements',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Restaurant Ambiance',
          caption: 'Atmospheric dining lighting with zones',
          image: 'https://placehold.co/600x400'
        }
      ],
      caseStudy: {
        title: 'Metropolitan Convention Center - 500,000 sq ft Lighting Overhaul',
        description: 'We redesigned the complete lighting system for a major convention center, reducing energy consumption by 65% while improving illumination quality by 40%. Our Dialux analysis identified optimal fixture placement and LED specifications that met stringent event lighting requirements. The project achieved LEED Platinum certification and saves the facility $340,000 annually in energy costs.',
        image: 'https://placehold.co/600x400'
      }
    },
    'floor-plans': {
      title: 'Floor Plan Solutions',
      subtitle: 'Detailed architectural floor plans with accurate dimensions and space optimization',
      heroImage: 'https://placehold.co/600x400',
      description: {
        what: 'Our floor plan services deliver precise architectural layouts with accurate dimensions, furniture arrangements, and space optimization. We create detailed plans that serve as the foundation for construction, permitting, and interior design processes.',
        who: 'Perfect for architects planning new constructions, interior designers optimizing spaces, real estate developers marketing properties, and homeowners planning renovations.',
        why: 'Accurate floor plans are essential for project success, cost estimation, and regulatory approval. Our expertise ensures optimal space utilization, compliance with building codes, and clear communication between all project stakeholders.'
      },
      valueProps: [
        {
          icon: 'ri-ruler-line',
          title: 'Precise Dimensions',
          description: 'Accurate measurements verified for construction use'
        },
        {
          icon: 'ri-layout-grid-line',
          title: 'Space Optimization',
          description: 'Maximized functionality and flow in every layout'
        },
        {
          icon: 'ri-shield-check-line',
          title: 'Code Compliance',
          description: 'Meeting all building codes and accessibility requirements'
        },
        {
          icon: 'ri-home-4-line',
          title: 'Furniture Planning',
          description: 'Integrated furniture layouts and space planning'
        },
        {
          icon: 'ri-file-text-line',
          title: 'Permit Ready',
          description: 'Documentation ready for permit submissions'
        }
      ],
      portfolioProjects: [
        {
          title: 'Luxury Penthouse',
          caption: 'High-end residential with custom layouts',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Medical Clinic',
          caption: 'Specialized healthcare facility planning',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Restaurant Design',
          caption: 'Hospitality space with optimal seating flow',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Office Renovation',
          caption: 'Corporate workspace optimization',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Retail Store Layout',
          caption: 'Commercial space for maximum customer flow',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Multi-Family Housing',
          caption: 'Residential complex with unit variations',
          image: 'https://placehold.co/600x400'
        }
      ],
      caseStudy: {
        title: 'Urban Loft Conversion - Historic Building Adaptive Reuse',
        description: 'We transformed a 1920s warehouse into 24 luxury loft units, optimizing each 1,200-2,400 sq ft space for modern living while preserving historic character. Our detailed floor plans accommodated varying ceiling heights, exposed structural elements, and modern amenities. The project achieved 100% pre-sales and won the Historic Preservation Award for adaptive reuse excellence.',
        image: 'https://placehold.co/600x400'
      }
    },
    'line-diagrams': {
      title: 'Line Diagram Solutions',
      subtitle: 'Technical line diagrams and electrical schematics with precision documentation',
      heroImage: 'https://placehold.co/600x400',
      description: {
        what: 'Our line diagram services provide comprehensive technical schematics, electrical diagrams, and system layouts for complex engineering projects. We create precise documentation that meets industry standards and facilitates efficient installation and maintenance.',
        who: 'Essential for electrical engineers designing systems, contractors installing equipment, facility managers maintaining infrastructure, and manufacturers documenting products.',
        why: 'Accurate technical diagrams prevent costly installation errors, ensure safety compliance, and provide essential documentation for maintenance and troubleshooting. Our precision saves time and reduces project risks significantly.'
      },
      valueProps: [
        {
          icon: 'ri-git-branch-line',
          title: 'System Clarity',
          description: 'Clear technical documentation for complex systems'
        },
        {
          icon: 'ri-shield-check-line',
          title: 'Safety Compliance',
          description: 'Meeting all electrical and safety code requirements'
        },
        {
          icon: 'ri-tools-line',
          title: 'Installation Ready',
          description: 'Detailed diagrams for efficient installation processes'
        },
        {
          icon: 'ri-file-list-3-line',
          title: 'Comprehensive Documentation',
          description: 'Complete technical specifications and component lists'
        },
        {
          icon: 'ri-search-line',
          title: 'Maintenance Support',
          description: 'Clear diagrams for troubleshooting and maintenance'
        }
      ],
      portfolioProjects: [
        {
          title: 'Manufacturing Control System',
          caption: 'Industrial automation and control diagrams',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Data Center Infrastructure',
          caption: 'Power distribution and network schematics',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Solar Power Installation',
          caption: 'Renewable energy system documentation',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Hospital Emergency Systems',
          caption: 'Critical power and backup system diagrams',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Smart Building Automation',
          caption: 'Integrated building systems and controls',
          image: 'https://placehold.co/600x400'
        },
        {
          title: 'Telecommunications Hub',
          caption: 'Network infrastructure and connectivity',
          image: 'https://placehold.co/600x400'
        }
      ],
      caseStudy: {
        title: 'International Airport Terminal - Critical Systems Integration',
        description: 'We developed comprehensive technical diagrams for a new international terminal\'s critical systems including power distribution, fire safety networks, baggage handling automation, and passenger information systems. Our detailed schematics enabled seamless coordination between 12 different contractors and systems integrators. The project was completed 3 months ahead of schedule with zero system integration conflicts.',
        image: 'https://placehold.co/600x400'
      }
    }
  };

  // Get current service or default to AutoCAD
  const currentService = serviceData[serviceName || 'autocad'] || serviceData['autocad'];



  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 transition-colors duration-300">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-[#dbcfbf]/30 to-white dark:from-slate-800/50 dark:to-slate-900 transition-colors duration-300">
          <div
            className="absolute inset-0 opacity-15"
            style={{
              backgroundImage: `url('${currentService.heroImage}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#212026] dark:text-gray-100 mb-6 transition-colors duration-300">
              {currentService.title}
            </h1>
            <p className="text-xl md:text-2xl text-[#889d8f] dark:text-gray-300 max-w-4xl mx-auto transition-colors duration-300">
              {currentService.subtitle}
            </p>
          </div>
        </section>

        {/* Service Description Section */}
        <section className="py-20 bg-white dark:bg-slate-900 transition-colors duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-[#212026] dark:text-gray-100 mb-8 transition-colors duration-300">
                  What We Deliver
                </h2>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-[#e18033] mb-3">What it is</h3>
                    <p className="text-[#889d8f] dark:text-gray-300 leading-relaxed transition-colors duration-300">
                      {currentService.description.what}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#e18033] mb-3">Who benefits</h3>
                    <p className="text-[#889d8f] dark:text-gray-300 leading-relaxed transition-colors duration-300">
                      {currentService.description.who}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#e18033] mb-3">Why it matters</h3>
                    <p className="text-[#889d8f] dark:text-gray-300 leading-relaxed transition-colors duration-300">
                      {currentService.description.why}
                    </p>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img
                  src={`https://placehold.co/600x400`}
                  alt="Service Description"
                  className="w-full h-96 object-cover object-top rounded-2xl shadow-xl dark:shadow-slate-900/50"
                />
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#e18033] rounded-2xl flex items-center justify-center shadow-xl dark:shadow-slate-900/50">
                  <div className="text-center text-white">
                    <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                      <i className="ri-award-line text-2xl"></i>
                    </div>
                    <div className="text-sm font-semibold">Expert</div>
                    <div className="text-sm">Service</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Portfolio Showcase Section */}
        <section className="py-20 bg-[#dbcfbf]/20 dark:bg-slate-800/50 transition-colors duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#212026] dark:text-gray-100 mb-4 transition-colors duration-300">
                Portfolio Showcase
              </h2>
              <p className="text-xl text-[#889d8f] dark:text-gray-300 max-w-3xl mx-auto transition-colors duration-300">
                See our expertise in action through successful project examples
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {currentService.portfolioProjects.map((project: any, index: number) => (
                <Card
                  key={index}
                  className="group overflow-hidden cursor-pointer bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 transition-colors duration-300"
                  onClick={() => setSelectedProject(selectedProject === index ? null : index)}
                >
                  <div className="relative h-48 bg-[#dbcfbf]/10 dark:bg-slate-700/30">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#212026]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute bottom-4 left-4 right-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2 bg-[#e18033]/80 rounded-full">
                        <i className="ri-eye-line text-lg"></i>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-[#212026] dark:text-gray-100 mb-2 group-hover:text-[#e18033] transition-colors duration-300">
                      {project.title}
                    </h3>
                    <p className="text-[#889d8f] dark:text-gray-300 text-sm transition-colors duration-300">
                      {project.caption}
                    </p>
                  </div>

                  {/* Expanded View */}
                  {selectedProject === index && (
                    <div className="fixed inset-0 bg-[#212026]/80 dark:bg-black/90 flex items-center justify-center z-50 p-4 transition-colors duration-300">
                      <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-auto transition-colors duration-300">
                        <div className="relative">
                          <img
                            src={project.image}
                            alt={project.title}
                            className="w-full h-64 md:h-96 object-cover object-top"
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedProject(null);
                            }}
                            className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-[#e18033]/60 rounded-full flex items-center justify-center cursor-pointer transition-colors duration-300"
                          >
                            <div className="w-6 h-6 flex items-center justify-center">
                              <i className="ri-close-line text-white text-xl"></i>
                            </div>
                          </button>
                        </div>
                        <div className="p-8">
                          <h3 className="text-2xl font-bold text-[#212026] dark:text-gray-100 mb-4 transition-colors duration-300">
                            {project.title}
                          </h3>
                          <p className="text-[#889d8f] dark:text-gray-300 text-lg leading-relaxed transition-colors duration-300">
                            {project.caption}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Downloadable Resource Section */}
        {/* <section className="py-20 bg-white dark:bg-slate-900 transition-colors duration-300">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[#212026] dark:text-gray-100 mb-6 transition-colors duration-300">
              Get Detailed Information
            </h2>
            <p className="text-xl text-[#889d8f] dark:text-gray-300 mb-8 max-w-2xl mx-auto transition-colors duration-300">
              Download our comprehensive brochure to learn more about our {currentService.title.replace(' Solutions', '').toLowerCase()} capabilities and pricing
            </p>
            <Card className="p-8 max-w-md mx-auto bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 transition-colors duration-300">
              <div className="w-16 h-16 bg-[#e18033]/10 dark:bg-[#e18033]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-file-pdf-line text-[#e18033] text-2xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-bold text-[#212026] dark:text-gray-100 mb-2 transition-colors duration-300">
                {currentService.title} Brochure
              </h3>
              <p className="text-[#889d8f] dark:text-gray-300 mb-6 transition-colors duration-300">
                Comprehensive overview of our capabilities, process, and pricing
              </p>
              <Button variant="primary" size="lg" className="w-full bg-[#e18033] hover:bg-[#d16f29] dark:bg-[#e18033] dark:hover:bg-[#d16f29] transition-all duration-300">
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-download-line"></i>
                </div>
                Download PDF Brochure
              </Button>
            </Card>
          </div>
        </section> */}

        {/* Call-to-Action Section */}
        <section className="py-20 bg-[#e18033] dark:bg-[#d16f29] transition-colors duration-300">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to get started with {currentService.title.replace(' Solutions', '')}?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Let our expert team deliver precision {currentService.title.replace(' Solutions', '').toLowerCase()} services tailored to your project needs
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">

              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-[#e18033] dark:border-gray-300 dark:hover:bg-gray-300 dark:hover:text-slate-900 transition-all duration-300">
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-phone-line"></i>
                </div>
                Contact Us
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}